// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.GridChart.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

const NUMBER_PER_ROW = 2;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 30, 40, 50];
const DEFAULT_PAGE_SIZE = 5;

import { PAGE_SIZE_BACK_SIDE } from 'logic/data/Izenda.ReportPartChartConstants';

import connect from 'share/enhancers/Izenda.Connect';
import { withState, withProps, withHandlers } from 'recompose';
import { observer } from 'mobx-react';
import React, { Component } from 'react';
import classnames from 'classnames';
import {
  get,
  head,
  omit,
  isFunction,
  map,
  find,
  concat,
  size,
  isNil,
  isNaN,
  toNumber,
  reduce,
  slice,
  forEach,
  uniqueId,
  getOr,
  curry,
  isEmpty,
  some
} from 'lodash/fp';

import ChartContainer from './Izenda.ChartContainer';
import Paginator from 'share/components/controls/DataGrid/helpers/Izenda.Paginator';
import SimpleChart from './Izenda.SimpleChart';
import ChartPointAction from './Izenda.ChartPointAction';
import GridChartUtils from 'share/utils/Izenda.GridChartUtils';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import { UNDEFINED_VALUE, MOBILE_WIDTH_HEIGHT } from 'logic/data/Izenda.UIConstants';

const { hasCharts, buildChartTree, getNumChart, getGroupChartInfo, getSizePercent, shouldApplyMobileStyles } = GridChartUtils;

@observer
class ChartGroup extends Component {
  render() {
    const { groupName, groupLevels, chartOptions, seriesOptions, children,chartType, printingMode } = this.props;
    const chartSeries = this.props.chartSeries || [];
    const groupInfo = getGroupChartInfo(chartSeries, groupLevels);
    const groupField = get('groupField', groupInfo);
    const groupFieldName = get('fieldNameAlias', groupField);
    const pointAction = get('commonActions.pointAction', chartOptions);
    const fieldConfig = get(groupFieldName, seriesOptions);

    const classesHeader = classnames('izenda-ChartWrapper-header', {
      u_borderBottom: groupInfo && groupInfo.isShowPanel
    });

    const renderTitle = () => {
      const fieldSource = {
        element: get('groupField', groupInfo),
        record: head(get('records', groupInfo))
      };
      return (
        groupInfo && (
          <ChartPointAction
            value={groupName}
            configField={fieldConfig}
            pointAction={pointAction}
            fieldName={groupFieldName}
            fieldSource={fieldSource}
            linkTo={groupInfo.linkTo}
          />
        )
      );
    };

    const chartTypeContainerClass = !printingMode && shouldApplyMobileStyles(chartType)
      ? 'izenda-ChartWrapper-content izenda-ChartWrapper-content-mobile'
      : 'izenda-ChartWrapper-content';

    return (
      <div className='izenda-Wrapper grid-chart-item'>
        <div className={classesHeader}>{renderTitle()}</div>
        <div className={chartTypeContainerClass}>{children}</div>
      </div>
    );
  }
}

@observer
class ChartItem extends Component {
  render() {
    const { chart, printingMode, style, chartOptions: chartOpt, finishedRender } = this.props;
    const chartProps = omit(['style'], this.props);

    try {
      let chartOptions = isFunction(chart) ? chart() : chart;
      chartOptions = $izenda.extend(true, {}, chartOpt, chartOptions, {
        commonOptions: {
          izendaOptions: {
            autoResized: true
          }
        }
      });

      return (
        <div style={style} className='grid-chart-item'>
          <SimpleChart {...chartProps} chartOptions={chartOptions} />
        </div>
      );
    } catch (e) {
      if (typeof finishedRender === 'function') {
        finishedRender();
      }

      return (
        <div style={style} className='grid-chart-item'>
          {e.message}
        </div>
      );
    }
  }
}

@connect()
class GridItem extends Component {
  render() {
    const { groupName, itemPerRow, itemOrder, groupLevels, renderListeners, style , chartType, printingMode } = this.props;
    const renderGroup = chartTree => {
      const childGroups = map(childNodeName => {
        const numChartsByGroup = getNumChart(chartTree[childNodeName]);
        const itemPerRowByGroup = Math.min(numChartsByGroup, itemPerRow);
        const width = (100 / itemPerRow) * itemPerRowByGroup;
        const groupSize = getSizePercent(width, 100);

        return (
          <GridItem
            {...this.props}
            key={childNodeName}
            groupName={childNodeName}
            groupLevels={newGroupLevels}
            chartSeries={chartTree[childNodeName]}
            style={groupSize}
            numCharts={numChartsByGroup}
            itemPerRow={itemPerRowByGroup}
          />
        );
      }, itemOrder);

      return childGroups;
    };

    const renderChartSerie = chartNode => {
      const charts = chartNode.charts || [];
      const itemPerRowByGroup = itemPerRow;
      const chartSize = getSizePercent(100 / itemPerRowByGroup, 100);

      return charts.map((chart, index) => {
        const renderListener = find(listener => listener.partId === chart.id, renderListeners);
        return (
          <ChartItem
            {...omit(['renderListeners'], this.props)}
            key={index}
            groupLevels={newGroupLevels}
            style={chartSize}
            chart={chart}
            finishedRender={renderListener && renderListener.resolve}
          />
        );
      });
    };

    const getGroupLevels = (root, newLevel) => {
      return newLevel ? concat(root || [], newLevel) : root;
    };

    const chartSeries = this.props.chartSeries || [];
    const newGroupLevels = getGroupLevels(groupLevels, groupName);
    let content;

    if (hasCharts(chartSeries)) {
      content = renderChartSerie(chartSeries);
    } else if (size(chartSeries) === 1 && hasCharts(chartSeries[groupName])) {
      content = renderChartSerie(chartSeries[groupName]);
    } else {
      content = renderGroup(chartSeries);
    }

    if (!isNil(groupName)) {
      content = (
        <ChartGroup {...this.props} groupName={groupName}>
          {content}
        </ChartGroup>
      );
    }

    const resultStyle = !printingMode && shouldApplyMobileStyles(chartType)
      ? { width: MOBILE_WIDTH_HEIGHT, height: MOBILE_WIDTH_HEIGHT }
      : style;

    return (
      <section className='grid-chart-item' style={resultStyle}>
        {content}
      </section>
    );
  }
}

@observer
class Row extends Component {
  render() {
    const { chartSeries, itemPerRow, className, style } = this.props;
    const chartTree = buildChartTree(chartSeries);
    const chartNodes = chartTree.chartTree;
    const groupPathInOrder = chartTree.groupPathInOrder;
    const classes = classnames('izenda-GridChart_row', className);
    let content;

    const renderGroup = (groupName, charts, chartLevel) => {
      const chartSeries = chartNodes[groupName] || charts;
      const numCharts = getNumChart(chartSeries, 0);
      const groupSize = getSizePercent((100 / itemPerRow) * numCharts, 100);
      const grpName = isNaN(toNumber(groupName)) ? groupName : toNumber(groupName);
      const itemOrder = reduce(
        (filtered, item) => {
          item[0] === grpName && item.length > 1 && filtered.push(item[1]);
          return filtered;
        },
        [],
        groupPathInOrder
      );

      return (
        <GridItem
          {...this.props}
          chartLevel={chartLevel}
          gridLevel={0}
          key={groupName}
          chartSeries={chartSeries}
          style={groupSize}
          itemPerRow={numCharts}
          groupName={groupName}
          itemOrder={itemOrder}
        />
      );
    };

    if (hasCharts(chartNodes)) {
      content = renderGroup(null, chartNodes, 0);
    } else {
      const nodeNames = GridChartUtils.getGroupNameInOrder(chartTree.chartTree, chartTree.groups);

      content = map(renderGroup, nodeNames);
    }

    return (
      <div style={style} className={classes}>
        {content}
      </div>
    );
  }
}

@observer
class PageChart extends Component {
  render() {
    const { itemPerRow, numRows, chartSeries, printingMode, pagingMode, doesBreakPage } = this.props;
    const numCharts = size(chartSeries);
    const rows = getRows(chartSeries);

    // Calculate each separator size in a part
    const rowSize = getSizePercent(100, 100 / numRows);
    return (
      <div className='u_fullSize'>
        {rows.map((row, index) => {
          return (
            <Row
              {...this.props}
              itemPerRow={printingMode && pagingMode ? itemPerRow : Math.min(itemPerRow, numCharts)}
              key={index}
              style={rowSize}
              chartSeries={row}
            />
          );
        })}
      </div>
    );

    function getRows(chartSeries) {
      let chartsByRow;
      const rows = [];
      let counting = 0;

      while (counting < numCharts) {
        chartsByRow = slice(counting, counting + itemPerRow, chartSeries);
        rows.push(chartsByRow);
        counting += size(chartsByRow);
      }

      return rows;
    }
  }
}

class GridChartContainer extends Component {
  componentWillReceiveProps(nextProps: any) {
    const { chartSeries, goToPage } = this.props;

    if (nextProps.chartSeries !== chartSeries) {
      goToPage(1);
    }
  }

  shouldComponentUpdate(nextProps: any) {
    const { printingMode, fulfilledData } = this.props;
    return !(printingMode && fulfilledData === nextProps.fulfilledData);
  }

  componentDidUpdate() {
    this.finishedRenderChartSerie();
  }

  componentDidMount() {
    this.finishedRenderChartSerie();
  }

  finishedRenderChartSerie() {
    const { finishedRender, fulfilledData, renderListeners } = this.props;
    if (finishedRender && fulfilledData) {
      const promises = map(item => item.promise, renderListeners);

      Promise.all(promises).then(() => {
        finishedRender();
      });
    }
  }

  /**
   * Render for printing.
   */
  printingRender() {
    const {
      doesBreakPage,
      className,
      chartOptions,
      fulfilledData,
      chartData,
      chartType,
      seriesOptions,
      renderListeners,
      children,
      type,
      vizEngine
    } = this.props;
    let separator = this.props.separator;
    let pageNumber = toNumber(this.props.pageNumber);
    let elementsCountInContainer = toNumber(this.props.elementsCountInContainer);
    const pagingMode = pageNumber > 0;
    const classes = classnames('izenda-grid-chart u_fullWidth u_overflowVisible', className);
    const chartSeries = this.props.chartSeries || {};
    const chartSeriesGroups = [];
    let itemPerRow =
      get('view.izItemPerRow', chartOptions) || get('optionByType.izItemPerRow', chartOptions) || NUMBER_PER_ROW;

    const chartWrapperClass = 'u_overflowVisible u_fullHeight';

    const sectionStyle = {
      float: 'none'
    };

    /**
     * Build chart series groups by separator.
     * This is intended to use for exporting with a given separator value.
     */
    const buildChartSeriesGroupsBySeparator = () => {
      const { groupCharts } = this.props;
      if (!this.props.forExporting) return groupCharts;
      // In exporting, if we do not pass value for separator then separator will be UNDEFINED_VALUE.
      isNil(separator) && (separator = UNDEFINED_VALUE);
      const selectedGroupCharts = {};
      forEach(groupName => {
        if (groupName === separator) {
          selectedGroupCharts[groupName] = groupCharts[groupName];
        }
      }, Object.keys(groupCharts));

      return isEmpty(selectedGroupCharts) ? groupCharts : selectedGroupCharts;
    };

    /**
     * Build chart collection of a group.
     */
    const buildChartSeriesGroup = (groupChart, chartSeries) => {
      const result = [];

      if (!groupChart) return result;

      forEach(info => {
        const chartData = find(data => data.id === info.id, chartSeries);
        chartData && result.push(chartData);
      }, groupChart);

      return result;
    };

    if (doesBreakPage) {
      const selectedGroupCharts = buildChartSeriesGroupsBySeparator();

      forEach(groupChart => {
        chartSeriesGroups.push(buildChartSeriesGroup(selectedGroupCharts[groupChart], chartSeries));
      }, Object.keys(selectedGroupCharts));

    } else {
      chartSeriesGroups.push(chartSeries);
    }

    if(pagingMode){
      chartSeriesGroups[0] = getChartPerPage(chartSeriesGroups[0], pageNumber, elementsCountInContainer);
      forEach(listener => {
        if(!some(chart => chart.id === listener.partId, chartSeriesGroups[0])){
          listener.resolve();
        }
      }, renderListeners);
    }    
    let total = size(chartSeriesGroups[0]);

    let numRows = Math.ceil(total / itemPerRow);

    const components = [];
    const numOfGroups = chartSeriesGroups.length - 1;
    chartSeriesGroups.map((series, index) => {
      components.push(
        <PageChart
          printingMode
          pagingMode={pagingMode}
          type={type}
          vizEngine={vizEngine}
          doesBreakPage={doesBreakPage}
          fulfilledData={fulfilledData}
          chartData={chartData}
          chartOptions={chartOptions}
          chartType={chartType}
          seriesOptions={seriesOptions}
          renderListeners={renderListeners}
          chartSeries={series}
          itemPerRow={itemPerRow}
          numRows={numRows}
          key={index}
        />
      );
      index !== numOfGroups &&
        doesBreakPage &&
        components.push(<div className='u_alwaysBreakAfter' key={uniqueId()} />);
    });

    try {
      return (
        <section className={classes} style={doesBreakPage ? sectionStyle : null}>
          <ChartContainer {...this.props}>
            {children}
            <div className={chartWrapperClass}>{components}</div>
          </ChartContainer>
        </section>
      );
    } catch (e) {
      return <span>Can't draw chart</span>;
    }
  }

  render() {
    const {
      printingMode,
      className,
      pageIndex,
      goToPage,
      changePageSize,
      viewMode,
      chartOptions,
      chartSeries,
      pageSize: pSize,
      children
    } = this.props;

    if (printingMode) return this.printingRender();

    const classes = classnames('izenda-grid-chart u_fullHeight u_fullWidth', className);
    let itemPerRow =
      get('view.izItemPerRow', chartOptions) || get('optionByType.izItemPerRow', chartOptions) || NUMBER_PER_ROW;
    const izUsePagination = getOr(false, 'optionByType.izUsePagination', chartOptions);
    const total = size(chartSeries);
    const pageSize = izUsePagination ? pSize : total;

    try {
      const chartsPerPage = getChartPerPage(chartSeries, pageIndex, pageSize);

      // item per row should be smaller than item per page;
      itemPerRow = Math.min(itemPerRow, pageSize);

      const numRows = Math.ceil(Math.min(pageSize, total) / itemPerRow);
      const chartBody = classnames('chart-body izenda-ChartWrapper-body');

      return (
        <section className={classes}>
          {/* metrics + chart */}
          <ChartContainer {...this.props}>
            {children}
            <div className={chartBody}>
              <PageChart
                {...this.props}
                chartSeries={chartsPerPage}
                itemPerRow={itemPerRow}
                numRows={numRows}
                key={pageIndex}
              />
            </div>

            {/* paginator */}
            {izUsePagination && renderPaginator()}
          </ChartContainer>
        </section>
      );
    } catch (e) {
      return <span>Can't draw chart</span>;
    }

    function renderPaginator() {
      return (
        <Paginator
          onChange={goToPage}
          onDropdownChange={changePageSize}
          totalRecords={total}
          pageSize={pageSize}
          pageIndex={pageIndex}
          canChangePageSize={!viewMode}
          pageSizes={PAGE_SIZE_OPTIONS}
        />
      );
    }
  }
}

function getChartPerPage(chartSeries, pageIndex, pageSize) {
  const start = (pageIndex - 1) * pageSize;
  const end = Math.min(start + pageSize, size(chartSeries));

  return chartSeries.slice(start, end);
}

/**
 * Change page size
 * @param {any} props
 * @param {number} pageSize
 */
function changePageSize(props, pageSize) {
  const setPageSize = get('commonActions.setPageSize', props.chartOptions);
  setPageSize && setPageSize(pageSize);
  props.updatePageSize && props.updatePageSize(pageSize);
  props.goToPage && props.goToPage(1);
}

export default connect(
  withProps(props => {
    let chartSeries;
    let charts;
    const renderListeners = [];

    try {
      chartSeries = props.getChartSeries();
    } catch (e) {
      chartSeries = {};
    }

    let { separator } = props;
    const groupCharts = getOr([], 'groupCharts', chartSeries);
    charts = getOr([], 'charts', chartSeries);

    isNil(separator) && (separator = UNDEFINED_VALUE);
    const selectedGroupCharts = groupCharts[separator];

    // build promises
    props.finishedRender &&
      forEach(chart => {
        // Only add render listeners to chart belong to selected separator group
        if (isEmpty(selectedGroupCharts) || some(({ id }) => id === chart.id, selectedGroupCharts)) {
          renderListeners.push(PrintingUtils.createPartRenderPromise(chart.id));
        }
      }, props.printingMode ? charts : slice(0, DEFAULT_PAGE_SIZE, charts));

    return {
      // only show 2 items in back-side
      chartSeries: props.isPreview && !props.printingMode ? slice(0, PAGE_SIZE_BACK_SIDE, charts) : charts,
      groupCharts: groupCharts,
      renderListeners: renderListeners
    };
  }),

  withState('pageIndex', 'goToPage', 1),
  withState('pageSize', 'updatePageSize', props => {
    return getOr(DEFAULT_PAGE_SIZE, 'staticProperties.pageSize', props.chartOptions);
  }),

  withHandlers({
    changePageSize: curry(changePageSize)
  })
)(GridChartContainer);



// WEBPACK FOOTER //
// ./ui/share/components/charting/Izenda.GridChart.js